import { TitlePublicationInput } from '../publications/Components/TitlePublicationInput';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MediaPublicationInput } from '../publications/Components/MediaPublicationInput';
import { ContentPublicationInput } from '../publications/Components/ContentPublicationInput';
import { useIATextGeneration } from '../publications/ia/useIATextGeneration';
import video from '../assets/video.png';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../rootReducer';
import emojiIcon from '../assets/emoji.svg';
import { LinkPublicationInput } from '../publications/Components/LinkPublicationInput';
import lien from '../assets/lien.png';
import Modal from '../modal/Modal';
import { ModalHeader } from './Modals/ModalHeader';
import { ModalFooter } from './Modals/ModalFooter';
import PublicationImageWithLogo from '../components/PublicationImageWithLogo';
import networkIcons from '../assets/network-render-icons.svg';
import {
    CategoryId,
    CategoryPublication,
    CategoryPublicationStatus,
    createCategoryPublication,
    deleteCategoryPublication,
    enrichCategoryPublicationWithMedias,
    syncCategoryPublicationMedias,
    updateCategoryPublication,
} from '../api/category';
import {
    isJpgMediaExtension,
    type Media,
    mediaUrlExtension,
    normalizeMediaExtension,
} from '../api/media';
import { ButtonLarge } from '../components/Button';
import {
    ALLOWED_CATEGORY_PUBLICATION_CAROUSEL_MEDIA_TYPES,
    ALLOWED_CATEGORY_PUBLICATION_MEDIA_TYPES,
} from './categoryMediaConstants';
import { useCategoryModalFileUpload } from './hooks/useCategoryModalFileUpload';
import {
    appendEmptyCarouselSlot,
    buildPublicationMediaSyncItems,
    carouselMediasFromPublication,
    computeRemovedCategoryPublicationMediaIds,
    emptyCarouselSlot,
    hasCategoryPrimaryMediaPreview,
    nextUploadSequenceFromPublication,
    previewMediasForCategoryPublicationForm,
    primaryMediaFromPublication,
    primaryPreviewFromMedia,
    publicationHasLink,
    type CategoryPublicationCarouselItem,
    toMediaCarouselView,
} from './categoryPublicationMedia';
import { Option } from '../types/option';
import { getCategoryFlowErrorMessage } from '../api/problemJson';
import {
    createPreviewObjectUrl,
    revokePreviewObjectUrl,
    revokePreviewObjectUrls,
} from '../utils/objectUrl';

type Props = {
    publication: Option<CategoryPublication>;
    categoryId: CategoryId;
    onPublicationSaved?: () => void;
};

const TAGS = ['@email', '@phone', '@website', '@name', '@address'];

export function CategoryPublicationForm({ publication, categoryId, onPublicationSaved }: Props) {
    const { userEmail, userSso, user, isAdmin, groupement } = useSelector(
        (state: RootState) => ({
            userEmail: state.user.email,
            userSso: state.user.isSsoUser,
            user: state.user.id,
            isAdmin: state.user.isAdmin,
            groupement: state.user.groupement,
        }),
        shallowEqual,
    );
    const isNetworkAdmin = !isAdmin && !userSso && groupement?.id !== 1;
    const [title, setTitle] = useState(publication.map(p => p.title).getOrElse(''));
    const [content, setContent] = useState(publication.map(p => p.content).getOrElse(''));
    const [link, setLink] = useState(publication.map(p => p.link).getOrElse(''));
    const [publicationStatus, setPublicationStatus] = useState(
        publication.map(p => p.status).getOrElse(CategoryPublicationStatus.DRAFT),
    );

    const [media, setMedia] = useState('');
    const [extension, setExtension] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [saveError, setSaveError] = useState<Option<string>>(Option.none());
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [contentCurrentPosition, setContentCurrentPosition] = useState(0);
    const [selectedTag, setSelectedTag] = useState('');
    const [existingPrimaryMedia, setExistingPrimaryMedia] = useState<Option<Media>>(Option.none());
    const [selectedPrimaryFile, setSelectedPrimaryFile] = useState<Option<File>>(Option.none());
    const [primaryMediaKey, setPrimaryMediaKey] = useState('');
    const [carouselMedias, setCarouselMedias] = useState<CategoryPublicationCarouselItem[]>([]);
    const [carouselFiles, setCarouselFiles] = useState<Map<string, File>>(new Map());
    const [initialPublicationMedias, setInitialPublicationMedias] = useState<Media[]>([]);
    const [primaryUploadSequence, setPrimaryUploadSequence] = useState<Option<number>>(
        Option.none(),
    );
    const [fileFormatMessage, setFileFormatMessage] = useState('');
    const uploadSequenceRef = useRef(0);
    const loadedPublicationIdRef = useRef(0);
    const publicationLoadGenerationRef = useRef(0);

    const assignNextUploadSequence = useCallback(
        (existingSequence: Option<number>): number =>
            existingSequence.fold(
                () => uploadSequenceRef.current++,
                sequence => sequence,
            ),
        [],
    );

    const { openIAModal, isErrorVisible, iaTextModals } = useIATextGeneration({
        userSso,
        userId: user,
        isAdminForIa: isNetworkAdmin || isAdmin,
        onGeneratedText: setContent,
    });

    const resetPrimaryMedia = useCallback(() => {
        revokePreviewObjectUrl(media);
        revokePreviewObjectUrls(carouselMedias.map(item => item.url));
        setMedia('');
        setExtension('');
        setSelectedPrimaryFile(Option.none());
        setExistingPrimaryMedia(Option.none());
        setPrimaryMediaKey('');
        setPrimaryUploadSequence(Option.none());
        setCarouselMedias([]);
        setCarouselFiles(new Map());
        uploadSequenceRef.current = 0;
    }, [carouselMedias, media]);

    const onPrimaryUploaded = useCallback(
        (file: File) => {
            const sequence = assignNextUploadSequence(
                existingPrimaryMedia.fold(
                    () => primaryUploadSequence,
                    media => Option.some(media.priority),
                ),
            );

            setSelectedPrimaryFile(Option.some(file));
            setPrimaryUploadSequence(Option.some(sequence));
            setExistingPrimaryMedia(Option.none());
            setPrimaryMediaKey('pending');
            revokePreviewObjectUrl(media);
            setMedia(createPreviewObjectUrl(file));

            const fileExtension = normalizeMediaExtension(mediaUrlExtension(file.name));
            setExtension(fileExtension);
            setLink('');

            setCarouselMedias(
                isJpgMediaExtension(fileExtension) ? [{ ...emptyCarouselSlot() }] : [],
            );

            setCarouselFiles(new Map());
        },
        [assignNextUploadSequence, existingPrimaryMedia, media, primaryUploadSequence],
    );

    const onPrimaryInvalidType = useCallback((userMessage: string) => {
        setFileFormatMessage(userMessage);
        window.setTimeout(() => setFileFormatMessage(''), 5000);
    }, []);

    const onInvalidPrimaryType = useCallback(() => {
        onPrimaryInvalidType(
            'Format de fichier non supporté. Seuls les fichiers .jpg et .mp4 sont acceptés.',
        );
    }, [onPrimaryInvalidType]);

    const { handleMediaChange: handlePrimaryMediaChange } = useCategoryModalFileUpload({
        allowedMimeTypes: ALLOWED_CATEGORY_PUBLICATION_MEDIA_TYPES,
        onUploaded: onPrimaryUploaded,
        onInvalidType: onInvalidPrimaryType,
        onClear: resetPrimaryMedia,
    });

    useEffect(() => {
        publication.fold(
            () => {
                loadedPublicationIdRef.current = 0;
                publicationLoadGenerationRef.current += 1;
                setInitialPublicationMedias([]);
            },
            currentPublication => {
                if (loadedPublicationIdRef.current === currentPublication.id) {
                    return;
                }

                loadedPublicationIdRef.current = currentPublication.id;
                const loadGeneration = publicationLoadGenerationRef.current + 1;
                publicationLoadGenerationRef.current = loadGeneration;

                setSelectedPrimaryFile(Option.none());
                setCarouselFiles(new Map());

                void (async () => {
                    const publicationWithMedias =
                        currentPublication.medias.length > 0
                            ? currentPublication
                            : await enrichCategoryPublicationWithMedias(currentPublication);

                    if (publicationLoadGenerationRef.current !== loadGeneration) {
                        return;
                    }

                    setTitle(publicationWithMedias.title ?? '');
                    setContent(publicationWithMedias.content ?? '');
                    setLink(publicationWithMedias.link ?? '');
                    setPublicationStatus(publicationWithMedias.status);
                    setInitialPublicationMedias(publicationWithMedias.medias);

                    uploadSequenceRef.current =
                        nextUploadSequenceFromPublication(publicationWithMedias);

                    if (publicationHasLink(publicationWithMedias)) {
                        resetPrimaryMedia();
                        setMedia(lien);
                        return;
                    }

                    primaryMediaFromPublication(publicationWithMedias).fold(
                        () => {
                            resetPrimaryMedia();
                        },
                        primary => {
                            const preview = primaryPreviewFromMedia(primary);
                            setExistingPrimaryMedia(Option.some(primary));
                            setPrimaryUploadSequence(Option.some(primary.priority));
                            setPrimaryMediaKey(primary.url);
                            setMedia(preview.url);
                            setExtension(preview.extension);

                            const carousel = carouselMediasFromPublication(publicationWithMedias);

                            setCarouselMedias(
                                isJpgMediaExtension(preview.extension)
                                    ? appendEmptyCarouselSlot(carousel)
                                    : [],
                            );
                        },
                    );

                    setSaveError(Option.none());
                })();
            },
        );
    }, [publication, resetPrimaryMedia]);

    useEffect(() => {
        if (media && media !== lien) {
            return;
        }

        setExistingPrimaryMedia(Option.none());
        setPrimaryUploadSequence(Option.none());
        setPrimaryMediaKey('');
    }, [media]);

    useEffect(
        () => () => {
            revokePreviewObjectUrl(media);
            revokePreviewObjectUrls(carouselMedias.map(item => item.url));
        },
        [carouselMedias, media],
    );

    const handleCursorPosition = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
        setContentCurrentPosition(e.currentTarget.selectionStart);
    };

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleMediaChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>, _networkKey?: string, index?: number) => {
            if (typeof index !== 'number') {
                handlePrimaryMediaChange(e);
                return;
            }

            e.preventDefault();
            const file = e.target.files?.[0];
            if (!file) {
                return;
            }

            if (!ALLOWED_CATEGORY_PUBLICATION_CAROUSEL_MEDIA_TYPES.includes(file.type)) {
                onPrimaryInvalidType(
                    'Format de fichier non supporté. Seuls les fichiers .jpg sont acceptés pour le carousel.',
                );
                return;
            }

            const slot = carouselMedias[index];
            if (!slot) {
                return;
            }

            revokePreviewObjectUrl(slot.url);
            const previewUrl = createPreviewObjectUrl(file);
            const priority = slot.uploadSequence.fold(
                () => {
                    const next = uploadSequenceRef.current;
                    uploadSequenceRef.current += 1;
                    return next;
                },
                sequence => sequence,
            );

            setCarouselFiles(prev => new Map(prev).set(slot.localId, file));
            setCarouselMedias(prev => {
                const updated = [...prev];
                updated[index] = {
                    ...slot,
                    url: previewUrl,
                    fileName: file.name,
                    order: priority,
                    networkKey: Option.none(),
                    id: slot.id,
                    uploadSequence: Option.some(priority),
                };
                return appendEmptyCarouselSlot(updated);
            });
        },
        [carouselMedias, handlePrimaryMediaChange, onPrimaryInvalidType],
    );

    const resetGlobalMedia = useCallback(() => {
        resetPrimaryMedia();
    }, [resetPrimaryMedia]);

    const resetCarouselMedia = useCallback(
        (_networkKey?: string, index?: number) => {
            if (typeof index !== 'number') {
                return;
            }

            const removedItem = carouselMedias[index];
            if (!removedItem) {
                return;
            }

            revokePreviewObjectUrl(removedItem.url);
            setCarouselFiles(prev => {
                const next = new Map(prev);
                next.delete(removedItem.localId);
                return next;
            });
            setCarouselMedias(prev =>
                appendEmptyCarouselSlot(prev.filter((_, itemIndex) => itemIndex !== index)),
            );
        },
        [carouselMedias],
    );

    const handleLinkChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const v = e.target.value;
            setLink(v);
            setMedia(lien);
            if (v.trim() !== '') {
                resetPrimaryMedia();
            } else {
                setMedia('');
            }
        },
        [resetPrimaryMedia],
    );

    const handleAddTag = useCallback(
        (tag: string) => {
            const newText =
                content.slice(0, contentCurrentPosition) +
                tag +
                content.slice(contentCurrentPosition);
            setContent(newText);
            const next = contentCurrentPosition + tag.length;
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.selectionStart = next;
                    textareaRef.current.selectionEnd = next;
                    textareaRef.current.focus();
                }
            }, 0);
        },
        [content, contentCurrentPosition],
    );

    const handleTagChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const tag = e.target.value;
        if (tag) {
            setSelectedTag(tag);
            handleAddTag(tag);
            setSelectedTag('');
        }
    };

    const handleSave = useCallback(async () => {
        setSaveError(Option.none());
        setIsSaving(true);

        const payload = {
            title,
            content,
            link,
            status: publicationStatus,
        };

        try {
            const savedPublicationId = await publication.fold(
                async () => {
                    const data = await createCategoryPublication(categoryId, payload);
                    return data.id;
                },
                async p => {
                    await updateCategoryPublication(p.id, payload);
                    return p.id;
                },
            );

            const hasPrimaryMediaPreview = hasCategoryPrimaryMediaPreview(media, extension, link);
            const mediaSyncItems = buildPublicationMediaSyncItems({
                existingPrimaryMedia,
                selectedPrimaryFile,
                primaryUploadSequence,
                hasPrimaryMediaPreview,
                initialMedias: initialPublicationMedias,
                carouselItems: carouselMedias,
                carouselFiles,
            });
            const removedMediaIds = computeRemovedCategoryPublicationMediaIds({
                initialMedias: initialPublicationMedias,
                existingPrimaryMedia,
                selectedPrimaryFile,
                hasPrimaryMediaPreview,
                carouselItems: carouselMedias,
                carouselFiles,
            });

            if (mediaSyncItems.length > 0 || removedMediaIds.length > 0) {
                await syncCategoryPublicationMedias(
                    savedPublicationId,
                    mediaSyncItems,
                    removedMediaIds,
                );
            }
            onPublicationSaved?.();
        } catch (err) {
            setSaveError(
                Option.some(
                    getCategoryFlowErrorMessage(err, "L'enregistrement a échoué. Réessayez."),
                ),
            );
        } finally {
            setIsSaving(false);
        }
    }, [
        carouselFiles,
        carouselMedias,
        categoryId,
        content,
        existingPrimaryMedia,
        extension,
        initialPublicationMedias,
        link,
        media,
        onPublicationSaved,
        primaryUploadSequence,
        publication,
        publicationStatus,
        selectedPrimaryFile,
        title,
    ]);

    const handleDelete = useCallback(async () => {
        await publication
            .map(async p => {
                setSaveError(Option.none());
                setIsDeleting(true);

                try {
                    await deleteCategoryPublication(p.id);
                    onPublicationSaved?.();
                } catch (err) {
                    setSaveError(
                        Option.some(
                            getCategoryFlowErrorMessage(err, 'La suppression a échoué. Réessayez.'),
                        ),
                    );
                } finally {
                    setIsDeleting(false);
                }
            })
            .getOrElse(Promise.resolve());
    }, [onPublicationSaved, publication]);

    const previewImage = extension === 'mp4' ? video : media;
    const primaryPreviewPriority = primaryUploadSequence.getOrElse(
        existingPrimaryMedia.map(media => media.priority).getOrElse(0),
    );

    const modalPreviewMedias = useMemo(
        () =>
            previewMediasForCategoryPublicationForm({
                primaryPreviewUrl: previewImage,
                extension,
                primaryPriority: primaryPreviewPriority,
                carouselItems: carouselMedias,
            }),
        [carouselMedias, extension, previewImage, primaryPreviewPriority],
    );

    return (
        <div className="flex w-full flex-col gap-3 rounded-md bg-white p-6">
            {fileFormatMessage && (
                <p
                    className="fixed bottom-6 left-1/2 z-[1001] -translate-x-1/2 rounded-3xl bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-lg"
                    role="status"
                >
                    {fileFormatMessage}
                </p>
            )}
            {saveError
                .map(e => (
                    <p
                        className="mx-auto mb-2 w-full max-w-2xl rounded-2xl bg-red-50 px-3 py-2 text-center text-small text-red-800"
                        role="alert"
                    >
                        {e}
                    </p>
                ))
                .toNull()}
            <TitlePublicationInput
                title={title}
                setTitle={setTitle}
                isViewOnly={false}
                error={''}
            />
            <div className="mb-6 flex w-full flex-col justify-between gap-5 md:flex-row">
                <MediaPublicationInput
                    media={media}
                    extension={extension}
                    handleMediaChange={handleMediaChange}
                    resetMedia={resetGlobalMedia}
                    resetMediaSmall={resetCarouselMedia}
                    mediaUrl={primaryMediaKey}
                    medias={toMediaCarouselView(carouselMedias)}
                    link={link}
                    isViewOnly={false}
                    isGlobal={true}
                />
                <ContentPublicationInput
                    content={content}
                    textareaRef={textareaRef}
                    setContent={setContent}
                    handleCursorPosition={handleCursorPosition}
                    handleAIModal={openIAModal}
                    handleTagChange={handleTagChange}
                    selectedTag={selectedTag}
                    isUserSso={userSso}
                    tags={TAGS}
                    emoji={emojiIcon}
                    isViewOnly={false}
                    error={''}
                    isErrorVisible={isErrorVisible}
                />
            </div>
            <div className={'flex w-full gap-5'}>
                <div className={'w-1/2'}>
                    <LinkPublicationInput
                        link={link}
                        handleLinkChange={handleLinkChange}
                        media={media}
                        isViewOnly={false}
                    />
                </div>
                <div className={'w-1/2'}>
                    <label htmlFor="status">Statut de publication</label>
                    <select
                        name="status"
                        id="status"
                        className={'w-full text-gray'}
                        value={publicationStatus}
                        onChange={e => {
                            setPublicationStatus(Number(e.target.value));
                        }}
                    >
                        <option value="0">Brouillon</option>
                        <option value="1">Statut terminée</option>
                    </select>
                </div>
            </div>
            <div className={'mt-4 flex items-center justify-center gap-6'}>
                <ButtonLarge
                    isPrimary={false}
                    type="button"
                    onClick={() => setIsDeleteConfirmOpen(true)}
                    disabled={publication.isEmpty() || isSaving || isDeleting}
                >
                    {isDeleting ? 'Suppression…' : 'Supprimer'}
                </ButtonLarge>
                <ButtonLarge
                    isPrimary={false}
                    type="button"
                    onClick={() => setIsPreviewModalOpen(true)}
                    disabled={isSaving || isDeleting}
                >
                    Prévisualiser
                </ButtonLarge>
                <ButtonLarge
                    isPrimary={true}
                    type="button"
                    onClick={() => void handleSave()}
                    disabled={isSaving || isDeleting}
                >
                    {isSaving ? 'Enregistrement…' : 'Enregistrer'}
                </ButtonLarge>
            </div>

            <Modal
                isOpen={isDeleteConfirmOpen}
                onClose={() => {
                    if (!isDeleting) {
                        setIsDeleteConfirmOpen(false);
                    }
                }}
                className="w-full min-h-48"
            >
                <div className="flex w-full flex-col items-center gap-3">
                    <ModalHeader>Supprimer la publication</ModalHeader>
                    <p className="max-w-md text-center text-sm font-medium">{title}</p>
                    <div className="w-full max-w-[min(280px,100%)] overflow-hidden rounded-[10px] bg-white shadow-[0px_7px_9px_rgba(141,141,141,0.10),_0px_4px_13px_rgba(141,141,141,0.08)] sm:max-w-[320px] md:max-w-[360px] md:rounded-[12px] lg:max-w-[400px]">
                        <span className="mt-2 flex h-[20px] w-full items-center px-1.5 text-[9px] font-light sm:mt-2.5 sm:h-[24px] sm:px-2 md:mt-3 md:h-[28px] md:px-2.5 md:text-xs">
                            {userEmail}
                        </span>
                        <PublicationImageWithLogo
                            imageUrl={previewImage}
                            alt="illustration"
                            className="flex h-[280px] w-full items-center justify-center bg-light-gray sm:h-[320px] md:h-[360px] lg:h-[400px]"
                            isGlobal={true}
                            showLogo={true}
                            extension={extension}
                            medias={modalPreviewMedias}
                            videoAsset={video}
                        />
                        <img
                            className="w-full px-1.5 py-1 sm:px-2 sm:py-1.5 md:px-2.5 md:py-2"
                            src={networkIcons}
                            alt="icons"
                        />
                        <span
                            className="mb-2 line-clamp-2 block w-full max-w-full overflow-hidden text-ellipsis px-1.5 text-justify text-[9px] font-small text-dark-gray sm:mb-2.5 sm:line-clamp-2 sm:px-2 sm:pb-1.5 sm:text-[10px] md:line-clamp-3 md:px-2.5 md:pb-2 md:mb-3 md:text-xs"
                            style={{ display: '-webkit-box', WebkitBoxOrient: 'vertical' }}
                        >
                            {content}
                        </span>
                        {link.trim() !== '' && (
                            <span className="text-blue block w-full max-w-full truncate px-1.5 pb-1 text-[9px] font-small underline sm:px-2 sm:pb-1.5 sm:text-[10px] md:px-2.5 md:pb-2 md:text-xs">
                                {link}
                            </span>
                        )}
                    </div>
                    <ModalFooter
                        handlePrimaryAction={() => {
                            setIsDeleteConfirmOpen(false);
                            void handleDelete();
                        }}
                        handleSecondaryAction={() => setIsDeleteConfirmOpen(false)}
                        primaryActionTitle="Supprimer"
                        secondaryActionTitle="Annuler"
                        disabled={isDeleting}
                    />
                </div>
            </Modal>
            {iaTextModals}

            <Modal
                isOpen={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
                className={'w-full min-h-48'}
            >
                <div className="flex w-full flex-col items-center gap-1 sm:gap-2 md:gap-3">
                    <ModalHeader>Aperçu de la publication</ModalHeader>
                    <div className="w-full max-w-[min(280px,100%)] overflow-hidden rounded-[10px] bg-white shadow-[0px_7px_9px_rgba(141,141,141,0.10),_0px_4px_13px_rgba(141,141,141,0.08)] sm:max-w-[320px] md:max-w-[360px] md:rounded-[12px] lg:max-w-[400px]">
                        <span className="mt-2 flex h-[20px] w-full items-center px-1.5 text-[9px] font-light sm:mt-2.5 sm:h-[24px] sm:px-2 md:mt-3 md:h-[28px] md:px-2.5 md:text-xs">
                            {userEmail}
                        </span>
                        <PublicationImageWithLogo
                            imageUrl={previewImage}
                            alt="illustration"
                            className="flex h-[280px] w-full items-center justify-center bg-light-gray sm:h-[320px] md:h-[360px] lg:h-[400px]"
                            isGlobal={true}
                            showLogo={true}
                            extension={extension}
                            medias={modalPreviewMedias}
                            videoAsset={video}
                        />
                        <img
                            className="w-full px-1.5 py-1 sm:px-2 sm:py-1.5 md:px-2.5 md:py-2"
                            src={networkIcons}
                            alt="icons"
                        />
                        <span
                            className="mb-2 line-clamp-2 block w-full max-w-full overflow-hidden text-ellipsis px-1.5 text-justify text-[9px] font-small text-dark-gray sm:mb-2.5 sm:line-clamp-2 sm:px-2 sm:pb-1.5 sm:text-[10px] md:line-clamp-3 md:px-2.5 md:pb-2 md:mb-3 md:text-xs"
                            style={{ display: '-webkit-box', WebkitBoxOrient: 'vertical' }}
                        >
                            {content}
                        </span>
                        {link.trim() !== '' && (
                            <span className="text-blue block w-full max-w-full truncate px-1.5 pb-1 text-[9px] font-small underline sm:px-2 sm:pb-1.5 sm:text-[10px] md:px-2.5 md:pb-2 md:text-xs">
                                {link}
                            </span>
                        )}
                    </div>
                    <div className="mt-2 flex w-full flex-col justify-center gap-2 sm:mt-3 sm:flex-row sm:gap-3 md:mt-4 md:gap-4">
                        <ModalFooter
                            handlePrimaryAction={() => {
                                void handleSave();
                                setIsPreviewModalOpen(false);
                            }}
                            handleSecondaryAction={() => setIsPreviewModalOpen(false)}
                            primaryActionTitle="Enregistrer"
                            secondaryActionTitle="Annuler"
                            disabled={isSaving || isDeleting}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
}
